import { ComponentProps, FC } from 'react';

import { VariantProps, cva } from 'class-variance-authority';
import { AiOutlineClose } from 'react-icons/ai';
import { BsInfoCircle } from 'react-icons/bs';
import AsyncCreatableSelect from 'react-select/async-creatable';
import AsyncSelect from 'react-select/async';
import { components } from 'react-select';
import Tooltip from 'ui/components/common/Tooltip';
import { cn } from 'utils/helpers';
import { debounce } from 'lodash';

const asyncSelectControl = cva(
  [
    'pr-2',
    '!shadow-none',
    'border',
    'rounded-md',
    'text-sm leading-5 tracking-[0.56px]',
    'text-content',
    '!bg-fill-secondary',
    'disabled:border-fill-secondary disabled:text-disabled',
  ],
  {
    variants: {
      size: {
        sm: ['text-sm', 'h-[36px] !min-h-0 pl-1', 'pr-3'],
        md: ['text-sm', 'h-[44px] pl-1', 'pr-3'],
        lg: ['text-base', 'h-[52px] pl-[6px]', 'pr-[14px]'],
      },
      isFocused: {
        true: ['!border-primary-base'],
        false: ['!border-separator-secondary'],
      },
      state: {
        danger: ['!border-danger-base'],
        warning: ['!border-warning-base'],
        success: ['!border-success-base'],
      },
    },
    defaultVariants: {
      size: 'sm',
    },
  },
);

const subtext = cva(['leading-4 text-content mt-[7px]'], {
  variants: {
    state: {
      default: [],
      danger: ['!text-danger-base'],
      warning: ['!text-warning-base'],
      success: ['!text-success-base'],
    },
    size: {
      sm: 'text-xs',
      md: 'text-xs',
      lg: 'text-sm',
    },
  },
  defaultVariants: {
    state: 'default',
    size: 'sm',
  },
});

type PROPS = ComponentProps<AsyncCreatableSelect> &
  VariantProps<typeof asyncSelectControl> & {
    label?: string;
    labelSubText?: string;
    information?: string;
    required?: boolean;
    subText?: string;
    isCreatable?: boolean;
    inputClassName?: string;
  };

const CustomAsyncSelect: FC<PROPS> = ({
  name,
  information,
  required,
  label,
  state,
  subText,
  size,
  className,
  labelSubText = '(Optional)',
  isCreatable,
  inputClassName,
  onInputChange,
  ...props
}) => {
  const Select = isCreatable ? AsyncCreatableSelect : AsyncSelect;

  return (
    <div className={cn('space-y-2.5', className)}>
      {(label || information) && (
        <div className="flex items-center justify-between">
          {label ? (
            <label
              htmlFor={name}
              className="text-sm leading-[22px] font-semibold"
            >
              {label}
              {required ? (
                <span className="text-danger-base">*</span>
              ) : (
                <span className="text-xs ml-[3px] leading-[18px] text-content font-normal">
                  {labelSubText}
                </span>
              )}
            </label>
          ) : null}
          {information ? (
            <div className="text-content">
              <Tooltip title={information} placement="top">
                <BsInfoCircle className="w-4 h-4" />
              </Tooltip>
            </div>
          ) : null}
        </div>
      )}
      <Select
        classNames={{
          control({ isFocused }) {
            return cn(
              asyncSelectControl({ state, isFocused, size }),
              inputClassName,
            );
          },
          indicatorSeparator() {
            return 'hidden';
          },
          dropdownIndicator() {
            return '!hidden';
          },
        }}
        components={{
          ClearIndicator: (props) => {
            const {
              innerProps: { ref, ...restInnerProps },
            } = props;

            return (
              <div {...restInnerProps} ref={ref}>
                <AiOutlineClose
                  role="button"
                  className="w-5 h-5 hover:text-secondary-base"
                />
              </div>
            );
          },
          Option: (props) => {
            return (
              <components.Option
                {...props}
                className={cn(
                  props.isSelected && '!bg-fill-primary !text-secondary-dark',
                  'hover:!bg-fill-primary',
                )}
              >
                <div
                  className={cn(
                    'text-sm font-semibold',
                    'max-w-full truncate cursor-pointer',
                  )}
                  {...props.innerProps}
                >
                  {props.label}
                </div>
              </components.Option>
            );
          },
        }}
        cacheOptions
        isClearable
        defaultOptions
        menuPlacement="auto"
        isCreatable={false}
        {...(onInputChange && {
          onInputChange: debounce((val, actionMeta) => {
            onInputChange?.(val, actionMeta);
          }, 500),
        })}
        {...props}
      />
      {!!subText && (
        <div className={cn(subtext({ state, size }))}>{subText}</div>
      )}
    </div>
  );
};

export default CustomAsyncSelect;
